import {
  AppealStatus,
  AppealVersion,
  JuryStageVotes,
  V2JuryStage,
  V2Stage1Result,
  V2Stage2Result,
} from '../types/appeals';

/**
 * Helpers for the Jury V2 (two-stage voting) appeal flow.
 *
 * Compatibility rules implemented here (and relied on by all V2 UI):
 * - An appeal is only treated as V2 when `appeal_version === 2`. Past and
 *   in-flight appeals that predate the field (or any cached responses without
 *   it) fall back to V1 and keep rendering the legacy UI untouched.
 * - All helpers are null-safe against V1 rows, where every v2_* field is
 *   null / 0 / absent.
 */

interface VersionedAppeal {
  appeal_version?: AppealVersion | number | null;
}

/** Returns 2 only when the appeal explicitly declares V2; everything else is V1. */
export function getAppealVersion(appeal: VersionedAppeal | null | undefined): AppealVersion {
  return appeal?.appeal_version === 2 ? 2 : 1;
}

export function isV2Appeal(appeal: VersionedAppeal | null | undefined): boolean {
  return getAppealVersion(appeal) === 2;
}

/** Headline V2 outcome keys (matches the stats `by_v2_outcome` buckets). */
export type V2Outcome = 'upheld' | 'removed' | 'reduced' | 'increased' | 'made_unappealable';

const STAGE2_TO_OUTCOME: Record<V2Stage2Result, V2Outcome> = {
  remove: 'removed',
  reduce: 'reduced',
  increase: 'increased',
  made_unappealable: 'made_unappealable',
};

/**
 * Derives the headline outcome of a decided V2 appeal.
 * Returns null while the jury is still out (or for V1 rows, where both
 * results are always null).
 */
export function getV2Outcome(
  stage1Result: V2Stage1Result | null | undefined,
  stage2Result: V2Stage2Result | null | undefined,
): V2Outcome | null {
  if (stage2Result) return STAGE2_TO_OUTCOME[stage2Result];
  if (stage1Result === 'upheld') return 'upheld';
  return null;
}

export interface V2OutcomeMeta {
  /** Short label, e.g. for queue badges. */
  label: string;
  /** Whether the appeal ended approved (ban removed/reduced) or denied. */
  approved: boolean;
  /** Tailwind text color class. */
  text: string;
  /** Tailwind classes for badge-style rendering. */
  badge: string;
  /** Tailwind bg class for bar charts / split bars. */
  bar: string;
  /** One-line plain-English description of what happened to the ban. */
  description: string;
}

export const V2_OUTCOME_META: Record<V2Outcome, V2OutcomeMeta> = {
  upheld: {
    label: 'Upheld',
    approved: false,
    text: 'text-red-400',
    badge: 'bg-red-500/20 text-red-300 border-red-500/30',
    bar: 'bg-red-500',
    description: 'The jury upheld the ban as-is. The ban remains untouched.',
  },
  removed: {
    label: 'Removed',
    approved: true,
    text: 'text-green-400',
    badge: 'bg-green-500/20 text-green-300 border-green-500/30',
    bar: 'bg-green-500',
    description: 'The jury removed the ban. It is no longer enforced — the historical ban record is preserved.',
  },
  reduced: {
    label: 'Reduced',
    approved: true,
    text: 'text-teal-400',
    badge: 'bg-teal-500/20 text-teal-300 border-teal-500/30',
    bar: 'bg-teal-500',
    description: 'The jury reduced the ban length. The expiry was rewound from the original ban date.',
  },
  increased: {
    label: 'Extended',
    approved: false,
    text: 'text-orange-400',
    badge: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
    bar: 'bg-orange-500',
    description: 'The jury extended the ban. The expiry was pushed out beyond the original.',
  },
  made_unappealable: {
    label: 'Unappealable',
    approved: false,
    text: 'text-amber-400',
    badge: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    bar: 'bg-amber-500',
    description: 'The jury voted to increase a permanent ban — applied by making the ban unappealable instead.',
  },
};

interface BadgeAppeal extends VersionedAppeal {
  status: AppealStatus;
  v2_jury_stage?: V2JuryStage | null;
  v2_stage1_result?: V2Stage1Result | null;
  v2_stage2_result?: V2Stage2Result | null;
}

export interface QueueBadge {
  label: string;
  className: string;
}

/**
 * Queue / list badge per the V2 handoff table. Returns null when no badge
 * applies (e.g. V1 rows outside the jury phase, or V2 rows before the jury
 * phase, where v2_jury_stage is null by invariant).
 */
export function getV2QueueBadge(appeal: BadgeAppeal): QueueBadge | null {
  const version = getAppealVersion(appeal);

  if (version === 1) {
    if (appeal.status === 'in_jury') {
      return { label: 'V1 Jury', className: 'bg-gray-500/20 text-gray-300 border-gray-500/30' };
    }
    return null;
  }

  if (appeal.status === 'in_jury') {
    if (appeal.v2_jury_stage === 2) {
      return { label: 'V2 · Stage 2', className: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30' };
    }
    if (appeal.v2_jury_stage === 1) {
      return { label: 'V2 · Stage 1', className: 'bg-purple-500/20 text-purple-300 border-purple-500/30' };
    }
    // Endpoints that don't expose v2_jury_stage (e.g. by-user / by-ban rows)
    return { label: 'V2 Jury', className: 'bg-purple-500/20 text-purple-300 border-purple-500/30' };
  }

  const outcome = getV2Outcome(appeal.v2_stage1_result, appeal.v2_stage2_result);
  if (outcome && (appeal.status === 'approved' || appeal.status === 'denied')) {
    const meta = V2_OUTCOME_META[outcome];
    return {
      label: `${meta.approved ? '\u2714' : '\u2718'} ${meta.label}`,
      className: meta.badge,
    };
  }

  return null;
}

/** Tallies for one V2 voting stage. */
export function tallyStageVotes(votes: { vote: string | null }[]): Record<string, number> & { pending: number; voted: number } {
  const tally: Record<string, number> = {};
  let pending = 0;
  let voted = 0;
  for (const v of votes) {
    if (v.vote === null || v.vote === undefined) {
      pending++;
    } else {
      voted++;
      tally[v.vote] = (tally[v.vote] || 0) + 1;
    }
  }
  return { ...tally, pending, voted };
}

/** Defensive default so V1 / legacy payloads never crash the V2 section. */
export function normalizeJuryStageVotes(votes: JuryStageVotes | null | undefined): JuryStageVotes {
  return {
    stage1: votes?.stage1 ?? [],
    stage2: votes?.stage2 ?? [],
  };
}

/**
 * Formats a ban-length duration in seconds with day granularity,
 * e.g. 90061 -> "1d 1h 1m". (utils/time.ts formatDuration only does h/m/s.)
 */
export function formatBanLength(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || isNaN(seconds) || seconds < 0) return '—';
  if (seconds === 0) return '0s';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (secs > 0 && parts.length === 0) parts.push(`${secs}s`);
  return parts.join(' ');
}

/**
 * Converts a possibly seconds-based epoch into a Date, null-safe.
 * Ban expiry fields (`bans.expires`, v2 snapshots) are seconds-epoch while
 * jury vote timestamps are ms-epoch — the < 1e12 heuristic handles both.
 */
export function epochToDate(ts: number | null | undefined): Date | null {
  if (ts === null || ts === undefined || isNaN(ts)) return null;
  const ms = ts < 1e12 ? ts * 1000 : ts;
  const d = new Date(ms);
  return isNaN(d.getTime()) ? null : d;
}
